// src/contexts/ActivityContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Trip } from './ItineraryContext';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Types
export interface Activity {
  id: string;
  title: string;
  description: string;
  location: string;
  type: 'activity' | 'food' | 'accommodation' | 'transportation';
  cost?: number;
  duration?: string;
  imageUrl?: string;
  address?: string;
  website?: string;
  phone?: string;
  rating?: number;
  tags?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  tripId?: string;
  isSaved: boolean;
  source?: 'manual' | 'ai' | 'search';
}

interface ActivityFilters {
  searchTerm?: string;
  type?: string;
  minCost?: number;
  maxCost?: number;
  tags?: string[];
  savedOnly?: boolean;
  rating?: number;
}

interface ActivityContextType {
  activities: Activity[];
  savedActivities: Activity[];
  loading: boolean;
  error: string | null;
  fetchActivities: (tripId?: string) => Promise<void>;
  searchActivities: (query: string, filters?: ActivityFilters) => Promise<Activity[]>;
  generateActivities: (prompt: string, tripId?: string, trip?: Trip) => Promise<Activity[]>;
  saveActivity: (activity: Activity) => Promise<boolean>;
  unsaveActivity: (activityId: string) => Promise<boolean>;
  deleteActivity: (activityId: string) => Promise<boolean>;
  addToItinerary: (activityId: string, tripId: string, dayIndex: number, slotIndex: number) => Promise<boolean>;
  filters: ActivityFilters;
  setFilters: (filters: ActivityFilters) => void;
}

const ActivityContext = createContext<ActivityContextType | undefined>(undefined);

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

// Helper function to categorize Google Place types
const categorizePlace = (types: string[]): Activity['type'] => {
  if (types.includes('restaurant') || types.includes('cafe') || types.includes('food')) {
    return 'food';
  }
  if (types.includes('lodging') || types.includes('hotel')) {
    return 'accommodation';
  }
  if (types.includes('transit_station') || types.includes('airport')) {
    return 'transportation';
  }
  return 'activity';
};

// Helper function to estimate cost based on price_level
const estimateCost = (place: any): number | undefined => {
  const priceMap: { [key: number]: number } = {
    0: 10,   // Free/Inexpensive
    1: 25,   // Cheap
    2: 50,   // Moderate
    3: 100,  // Expensive
    4: 200   // Very Expensive
  };
  return place.price_level !== undefined ? priceMap[place.price_level] : undefined;
};

// Helper function to extract activities from text response
const extractActivitiesFromText = (text: string): any[] => {
  const activities = [];
  const lines = text.split('\n');
  let currentActivity: any = {};
  
  for (const line of lines) {
    if (line.includes('title:')) {
      if (Object.keys(currentActivity).length > 0) {
        activities.push(currentActivity);
        currentActivity = {};
      }
      currentActivity.title = line.split('title:')[1].trim();
    } else if (line.includes('description:')) {
      currentActivity.description = line.split('description:')[1].trim();
    } else if (line.includes('location:')) {
      currentActivity.location = line.split('location:')[1].trim();
    } else if (line.includes('type:')) {
      currentActivity.type = line.split('type:')[1].trim();
    } else if (line.includes('cost:')) {
      currentActivity.cost = line.split('cost:')[1].trim();
    } else if (line.includes('duration:')) {
      currentActivity.duration = line.split('duration:')[1].trim();
    } else if (line.includes('tags:')) {
      currentActivity.tags = line.split('tags:')[1].trim().split(',').map((tag: string) => tag.trim());
    }
  }
  
  if (Object.keys(currentActivity).length > 0) {
    activities.push(currentActivity);
  }
  
  return activities;
};

// Helper function to generate image URL
const generateImageUrl = async (title: string, destination: string): Promise<string> => {
  // For now, use Unsplash random images. In production, you might want to use a more specific image API
  return `https://source.unsplash.com/random/?${encodeURIComponent(destination)},${encodeURIComponent(title)}`;
};

export const ActivityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [savedActivities, setSavedActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ActivityFilters>({});

  // Fetch activities
  const fetchActivities = useCallback(async (tripId?: string) => {
    try {
      setLoading(true);
      
      let activitiesQuery;
      if (tripId) {
        activitiesQuery = query(collection(db, 'activities'), where('tripId', '==', tripId));
      } else {
        activitiesQuery = collection(db, 'activities');
      }
      
      const activitiesSnapshot = await getDocs(activitiesQuery);
      
      const activitiesData: Activity[] = [];
      
      activitiesSnapshot.forEach((doc) => {
        const data = doc.data();
        
        activitiesData.push({
          id: doc.id,
          title: data.title,
          description: data.description || '',
          location: data.location,
          type: data.type,
          cost: data.cost,
          duration: data.duration,
          imageUrl: data.imageUrl,
          address: data.address,
          website: data.website,
          phone: data.phone,
          rating: data.rating,
          tags: data.tags || [],
          createdAt: data.createdAt?.toDate(),
          tripId: data.tripId,
          isSaved: data.isSaved || false,
          source: data.source || 'manual'
        });
      });
      
      setActivities(activitiesData);
      setSavedActivities(activitiesData.filter(activity => activity.isSaved));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities. Please try again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Search activities
  const searchActivities = async (
     query: string,
     searchFilters?: ActivityFilters
  ): Promise<Activity[]> => {
     try {
        setLoading(true);

        // Build the API URL with your API key
        const baseUrl = 'https://maps.googleapis.com/maps/api/place/textsearch/json';
        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

        // Call your backend proxy to avoid exposing API key
        const response = await fetch(
           `${process.env.REACT_APP_API_URL}/api/places-search?query=${encodeURIComponent(query)}&key=${apiKey}`
        );
        const data = await response.json();

        if (!response.ok) {
           throw new Error(data.message || 'Failed to search activities');
        }

        // Transform Google Places results into Activity objects
        const activities: Activity[] = data.results.map((place: any) => ({
           id: place.place_id,
           title: place.name,
           description: place.formatted_address || '',
           location: place.formatted_address,
           type: categorizePlace(place.types),
           cost: estimateCost(place),
           rating: place.rating,
           imageUrl: place.photos
              ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${place.photos[0].photo_reference}&key=${apiKey}`
              : undefined,
           isSaved: false,
           source: 'search',
        }));

        // Apply filters if provided
        let filteredActivities = activities;
        if (searchFilters) {
           filteredActivities = activities.filter((activity) => {
              if (searchFilters.type && activity.type !== searchFilters.type) return false;
              if (searchFilters.minCost && activity.cost && activity.cost < searchFilters.minCost)
                 return false;
              if (searchFilters.maxCost && activity.cost && activity.cost > searchFilters.maxCost)
                 return false;
              if (searchFilters.rating && activity.rating && activity.rating < searchFilters.rating)
                 return false;
              return true;
           });
        }

        setActivities(filteredActivities);
        setSavedActivities(filteredActivities.filter((activity) => activity.isSaved));
        setLoading(false);
        return filteredActivities;
     } catch (err) {
        console.error('Error searching activities:', err);
        setError('Search failed. Please try again.');
        setLoading(false);
        return [];
     }
  };

  // Generate activities with Perplexity AI
  const generateActivities = async (
     prompt: string,
     tripId?: string,
     trip?: Trip
  ): Promise<Activity[]> => {
     try {
        setLoading(true);

        if (!trip) {
           throw new Error('Trip details are required for activity generation');
        }

        // Create a fallback destination if needed
        const destination = trip.destination || trip.title?.split('-')[0]?.trim() || 'Pittsburgh';

        // Update the trip object with the fallback destination if needed
        const updatedTrip = {
           ...trip,
           destination: destination,
        };

        // Log debug information

        // Make API call to backend proxy
        const response = await axios.post(
           `${process.env.REACT_APP_API_URL}/api/perplexity-generate`,
           {
              prompt: prompt,
              trip: updatedTrip,
           },
           {
              headers: {
                 'Content-Type': 'application/json',
              },
           }
        );

        if (!response.data || !response.data.activities) {
           throw new Error('Invalid response from activity generation API');
        }

        const generatedActivities = response.data.activities.map((activity: any) => ({
           ...activity,
           id: uuidv4(),
           source: 'ai' as const,
        }));

        setActivities(generatedActivities);
        setLoading(false);
        return generatedActivities;
     } catch (err) {
        console.error('Error generating activities:', err);
        setError('Failed to generate activities. Please try again.');
        setLoading(false);

        // Return mock activities as fallback with a default destination
        return getMockActivities('Popular Tourist Destination');
     }
  };

  // Helper function to generate mock activities
  const getMockActivities = (destination: string): Activity[] => {
    return [
      {
        id: `ai-${Date.now()}-1`,
        title: `${destination} Hidden Gem Tour`,
        description: `Discover the lesser-known attractions in ${destination} with a local guide.`,
        location: destination,
        type: 'activity',
        cost: 45,
        duration: '3 hours',
        imageUrl: `https://source.unsplash.com/random/?${encodeURIComponent(destination)},tour`,
        tags: ['local', 'hidden gem', 'guided'],
        isSaved: false,
        source: 'ai' as const
      },
      {
        id: `ai-${Date.now()}-2`,
        title: `Local Food Experience in ${destination}`,
        description: `Taste the authentic flavors of ${destination} with a curated food tour featuring local specialties.`,
        location: `${destination} City Center`,
        type: 'food',
        cost: 65,
        duration: '4 hours',
        imageUrl: `https://source.unsplash.com/random/?${encodeURIComponent(destination)},food`,
        tags: ['food', 'local cuisine', 'culinary'],
        isSaved: false,
        source: 'ai' as const
      },
      {
        id: `ai-${Date.now()}-3`,
        title: `${destination} Cultural Workshop`,
        description: `Immerse yourself in the local culture with an interactive workshop led by local artisans.`,
        location: `${destination} Cultural District`,
        type: 'activity',
        cost: 35,
        duration: '2 hours',
        imageUrl: `https://source.unsplash.com/random/?${encodeURIComponent(destination)},culture`,
        tags: ['culture', 'workshop', 'hands-on'],
        isSaved: false,
        source: 'ai' as const
      }
    ];
  };

  // Save activity
  const saveActivity = async (activity: Activity): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Prepare activity for Firestore
      const firestoreActivity = {
        ...activity,
        isSaved: true,
        updatedAt: Timestamp.now()
      };
      
      // Prepare activity for local state (with Date instead of Timestamp)
      const localActivity = {
        ...activity,
        isSaved: true,
        updatedAt: firestoreActivity.updatedAt.toDate()
      };
      
      if (activity.id.startsWith('ai-') || activity.id.startsWith('search-')) {
        // New activity from AI or search, create a new document
        const docRef = await addDoc(collection(db, 'activities'), {
          ...firestoreActivity,
          createdAt: Timestamp.now()
        });
        
        localActivity.id = docRef.id;
      } else {
        // Update existing activity
        await updateDoc(doc(db, 'activities', activity.id), {
          ...firestoreActivity,
        });
      }
      
      // Update local state with Date objects
      setActivities(prevActivities => 
        prevActivities.map(act => 
          act.id === activity.id ? localActivity : act
        )
      );
      
      // Update saved activities list with Date objects
      setSavedActivities(prev => {
        const exists = prev.some(act => act.id === localActivity.id);
        if (exists) {
          return prev.map(act => act.id === localActivity.id ? localActivity : act);
        }
        return [...prev, localActivity];
      });
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error saving activity:', err);
      setError('Failed to save activity. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Unsave activity
  const unsaveActivity = async (activityId: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      await updateDoc(doc(db, 'activities', activityId), {
        isSaved: false,
        updatedAt: Timestamp.now()
      });
      
      // Update local state
      setActivities(prevActivities => 
        prevActivities.map(act => 
          act.id === activityId ? {...act, isSaved: false} : act
        )
      );
      
      // Update saved activities list
      setSavedActivities(prev => prev.filter(act => act.id !== activityId));
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error unsaving activity:', err);
      setError('Failed to unsave activity. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Delete activity
  const deleteActivity = async (activityId: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      await deleteDoc(doc(db, 'activities', activityId));
      
      // Update local state
      setActivities(prevActivities => 
        prevActivities.filter(act => act.id !== activityId)
      );
      
      // Update saved activities list
      setSavedActivities(prev => prev.filter(act => act.id !== activityId));
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error deleting activity:', err);
      setError('Failed to delete activity. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Add activity to itinerary
  const addToItinerary = async (activityId: string, tripId: string, dayIndex: number, slotIndex: number): Promise<boolean> => {
    try {
      setLoading(true);
      
      // In a real app, this would update the itinerary in Firestore
      // For now, just log it

      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Error adding to itinerary:', err);
      setError('Failed to add to itinerary. Please try again.');
      setLoading(false);
      return false;
    }
  };

  const value = {
    activities,
    savedActivities,
    loading,
    error,
    fetchActivities,
    searchActivities,
    generateActivities,
    saveActivity,
    unsaveActivity,
    deleteActivity,
    addToItinerary,
    filters,
    setFilters
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export default ActivityContext;